import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../api/client';
import type { AllowedUser, UserRole } from '@audit/shared-types';

interface FormData {
  email: string;
  role: UserRole;
}

function extractErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as any).response;
    if (response?.data?.message) return String(response.data.message);
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export default function AllowedUsersPage() {
  const [users, setUsers] = useState<AllowedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({ email: '', role: 'user' });
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Fetch allowed users
  useEffect(() => {
    void fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/admin/allowed-users');
      setUsers(response.data.data || []);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to fetch users'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email.trim()) return;

    try {
      setSubmitting(true);
      setError(null);

      if (editingId) {
        // Update existing user
        await apiClient.patch(`/admin/allowed-users/${editingId}`, {
          role: formData.role,
        });
      } else {
        // Create new user
        await apiClient.post('/admin/allowed-users', {
          email: formData.email.toLowerCase().trim(),
          role: formData.role,
        });
      }

      setFormData({ email: '', role: 'user' });
      setEditingId(null);
      await fetchUsers();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to save user'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to remove this user?')) return;

    try {
      setError(null);
      await apiClient.delete(`/admin/allowed-users/${id}`);
      await fetchUsers();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to delete user'));
    }
  };

  const handleEdit = (user: AllowedUser) => {
    setFormData({ email: user.email, role: user.role });
    setEditingId(user.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancel = () => {
    setFormData({ email: '', role: 'user' });
    setEditingId(null);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Manage Allowed Users</h1>
        <p className="mt-2 text-gray-600">Control which users can access this application</p>
        <Link to="/admin" className="mt-2 inline-flex text-sm text-brand-600 hover:text-brand-700">
          Back to Admin
        </Link>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Add/Edit Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {editingId ? 'Update User Role' : 'Add New User'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              disabled={!!editingId}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-50 disabled:text-gray-500"
              placeholder="user@example.com"
            />
          </div>

          <div>
            <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <select
              id="role"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="user">User (Read-only)</option>
              <option value="admin">Admin (Manage Users)</option>
            </select>
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={submitting || !formData.email.trim()}
              className="px-4 py-2 bg-brand-600 text-white font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? 'Saving...' : editingId ? 'Update Role' : 'Add User'}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-200 text-gray-900 font-medium rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {loading ? (
          <div className="p-6 text-center text-gray-500">Loading users...</div>
        ) : users.length === 0 ? (
          <div className="p-6 text-center text-gray-500">No users added yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left px-6 py-3 font-semibold text-gray-900">Email</th>
                  <th className="text-left px-6 py-3 font-semibold text-gray-900">Role</th>
                  <th className="text-left px-6 py-3 font-semibold text-gray-900">Added By</th>
                  <th className="text-left px-6 py-3 font-semibold text-gray-900">Created At</th>
                  <th className="text-left px-6 py-3 font-semibold text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm text-gray-900">{user.email}</td>
                    <td className="px-6 py-3 text-sm">
                      <span
                        className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.role === 'admin'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {user.role === 'admin' ? 'Admin' : 'User'}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-600">{user.addedBy}</td>
                    <td className="px-6 py-3 text-sm text-gray-600">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-3 text-sm space-x-2">
                      <button
                        onClick={() => handleEdit(user)}
                        className="text-brand-600 hover:text-brand-700 font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(user.id)}
                        className="text-red-600 hover:text-red-700 font-medium"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
